import type { FieldConversion } from "types/dashboard.types"

import type {
  TelemetryFieldDef,
  TowerSegmentFieldName,
  TowerSegmentMetric,
} from "helpers/config/telemetryFieldDefs"
import {
  allEntityFields,
  frontComputedFields,
  towerSegmentFields,
} from "helpers/config/telemetryFieldDefs"

/**
 * Field-keyed lookups over telemetryFieldDefs.
 *
 * This module is deliberately a leaf — it imports nothing but the field defs — because the
 * plot defs consume it, and anything reaching back into `helpers/config/dashboards` would
 * close an import cycle (the defs are evaluated at module scope, so a cycle is a real TDZ
 * hazard, not just a lint error).
 */

const DEFAULT_DECIMAL_PLACES = 2

/**
 * Builds a per-segment field name. The segment comes from the tower's own settings, so these
 * names cannot be constants like the rest — this keeps the naming pattern in one place, next to
 * the `tower.<segment>.*` keys the resolver matches them against.
 */
export const segmentField = (
  segment: string,
  metric: TowerSegmentMetric,
): TowerSegmentFieldName => `tower.${segment}.${metric}.agg`

/**
 * The registry keyed by plain string, for looking up names that arrive at runtime — an alert's
 * configured fields, a record's `_field`. The registry itself keeps literal keys so that
 * referencing an unregistered constant fails to compile, which is why this view exists.
 */
const fieldDefsByName: Record<string, TelemetryFieldDef> = {
  ...allEntityFields,
  ...frontComputedFields,
}

const segmentDefsByKey: Record<string, TelemetryFieldDef> = towerSegmentFields

// Rebuilds the `tower.<segment>.*` key for a concrete field name (`tower.base.torsion.agg`
// -> `tower.<segment>.torsion.agg`) so per-segment fields can be looked up in
// towerSegmentFields.
const toSegmentFieldKey = (name: string): string | undefined => {
  const parts = name.split(".")
  if (parts.length < 3 || parts[0] !== "tower") return undefined
  return ["tower", "<segment>", ...parts.slice(2)].join(".")
}

// Resolves the def for any telemetry field, including the per-segment tower-health ones.
export const getFieldDef = (name: string): TelemetryFieldDef | undefined => {
  const def = fieldDefsByName[name]
  if (def) return def

  const segmentKey = toSegmentFieldKey(name)
  return segmentKey ? segmentDefsByKey[segmentKey] : undefined
}

/**
 * The field's stored unit — what an alert threshold is expressed in and what the telemetry
 * pickers label. Returns undefined for an unknown field; a known field with no unit
 * returns "".
 */
export const getFieldUnit = (name: string): string | undefined => getFieldDef(name)?.unit

// The unit the plotted value carries, which differs from the stored one whenever the field
// declares a conversion.
const getFieldDisplayUnit = (name: string): string | undefined => {
  const def = getFieldDef(name)
  if (!def) return undefined
  return def.conversion ? (def.displayUnit ?? def.unit) : def.unit
}

/**
 * How a plot should format the given fields' values.
 *
 * A single axis can only carry one unit, so this resolves only when every field agrees on
 * one; mixed, unknown or unit-less fields return undefined and leave the def's own literals
 * in place. The fragment is flat because xy/scatter views keep these under `axes.y` while
 * the stat views keep them directly on `properties`.
 */
export const getFieldFormat = (
  fields: string[],
): { suffix: string; formatUnits: boolean; decimalPlaces: number } | undefined => {
  const units = new Set(fields.map(getFieldDisplayUnit))
  const [suffix] = units
  if (units.size !== 1 || !suffix) return undefined

  const defs = fields.map(getFieldDef)

  return {
    suffix,
    formatUnits: defs.some((def) => def?.formatUnits === true),
    decimalPlaces:
      defs.reduce<number | undefined>(
        (max, def) =>
          def?.decimalPlaces !== undefined &&
          (max === undefined || def.decimalPlaces > max)
            ? def.decimalPlaces
            : max,
        undefined,
      ) ?? DEFAULT_DECIMAL_PLACES,
  }
}

/**
 * Conversions to apply before plotting, keyed by field, in the shape `formatTelemetryData`
 * expects. Undefined when none of the fields need one.
 */
export const getFieldConversions = (
  fields: string[],
): Record<string, FieldConversion> | undefined => {
  const entries = fields.flatMap((field) => {
    const conversion = getFieldDef(field)?.conversion
    return conversion ? [[field, conversion] as const] : []
  })

  return entries.length > 0 ? Object.fromEntries(entries) : undefined
}

/**
 * Applies the fields' shared conversion to a standalone value, so an alert threshold and its
 * reference line land in the same unit as the plotted data. Left untouched when the fields
 * disagree on the conversion, which is the same case where `getFieldFormat` refuses to label
 * the axis.
 */
export const convertFieldsValue = (fields: string[], value: number): number => {
  const conversions = new Set(fields.map((field) => getFieldDef(field)?.conversion))
  const [conversion] = conversions

  return conversions.size === 1 && conversion === "radiansToDegrees"
    ? (value * 180) / Math.PI
    : value
}
